import { faFolder, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Field from 'components/global/Field';
import { formatFileSize } from 'components/global/FileExplorer/fileExplorer.helpers';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  Accordion,
  ListGroup,
  Button,
  Row,
  Col,
  Card,
} from 'react-bootstrap';

const FileSelector = ({
  folders,
  onSelectionChange,
  initialSelectedDocs = [],
}) => {
  const [selectedDocs, setSelectedDocs] = useState(initialSelectedDocs);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [folderSelectedCounts, setFolderSelectedCounts] = useState({});

  const toggleFolder = (folderId) => {
    setExpandedFolders({
      ...expandedFolders,
      [folderId]: !expandedFolders[folderId],
    });
  };

  const expandAll = () => {
    const expandedState = {};
    folders.forEach(folder => {
      expandedState[folder.id] = true;
    });
    setExpandedFolders(expandedState);
  };

  const collapseAll = () => {
    setExpandedFolders({});
  };

  const handleDocumentCheckboxChange = (docId) => {
    const updated = [...selectedDocs];
    if (updated.includes(docId)) {
      updated.splice(updated.indexOf(docId), 1);
    } else {
      updated.push(docId);
    }
    setSelectedDocs(updated);
    if (onSelectionChange) {
      onSelectionChange(updated);
    }
  };

  const handleFolderSelectAllToggle = (folderId) => {
    const documentsInFolder = folders.find(folder => folder.id === folderId)?.documents;
    if (!documentsInFolder || documentsInFolder.length === 0) return;

    const docIdsInFolder = documentsInFolder.map(doc => doc.id);
    const allSelectedInFolder = documentsInFolder.every(doc => selectedDocs.includes(doc.id));

    setSelectedDocs(prevSelectedDocs => {
      let updatedSelectedDocs;
      if (allSelectedInFolder) {
        updatedSelectedDocs = prevSelectedDocs.filter(id => !docIdsInFolder.includes(id));
      } else {
        updatedSelectedDocs = [
          ...prevSelectedDocs,
          ...docIdsInFolder.filter(id => !prevSelectedDocs.includes(id)),
        ];
      }
      if (onSelectionChange) {
        onSelectionChange(updatedSelectedDocs);
      }
      return updatedSelectedDocs;
    });
  };

  useEffect(() => {
    const counts = {};
    folders.forEach(folder => {
      counts[folder.id] = selectedDocs.filter(doc => folder.documents.some(d => d.id === doc)).length;
    });
    setFolderSelectedCounts(counts);
  }, [selectedDocs]);

  if (!folders || folders.length === 0) {
    return (
      <div className="text-center p-32 bg-transparent border-0">
        <p className="mb-0">No folders available</p>
      </div>
    );
  }

  return (
    <>
      <Row className="mb-32 pb-16 align-items-center border-bottom border-gray-700">
        <Col>
          {selectedDocs.length} files selected
        </Col>
        <Col className="text-end d-flex align-items-center justify-content-end">
          <Button variant="transparent" size="sm" onClick={expandAll} className="me-8">
            <small className="text-white">Expand All</small>
          </Button>
          <Button variant="transparent" size="sm" onClick={collapseAll}>
            <small className="text-white">Collapse All</small>
          </Button>
        </Col>
      </Row>
      <Accordion defaultActiveKey="0" flush>
        {folders.map((folder) => (
          <Card key={folder.id} className="mb-24 bg-transparent border-0">
            <Card.Header
              className="d-flex align-items-center cursor-pointer bg-gray-800 border-0 "
              onClick={() => toggleFolder(folder.id)}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon
                icon={expandedFolders[folder.id] ? faFolderOpen : faFolder}
                className="me-16 text-white"
              />
              <span className="me-auto text-white">{folder.name}</span>
              <small className="text-info">
                {folderSelectedCounts[folder.id] || 0} files selected
              </small>
            </Card.Header>
            {expandedFolders[folder.id] && (
              <Card.Body className="bg-gray-700 border-bottom border-gray-800">
                <Row className="border-bottom border-gray-900 pb-16">
                  <Col>
                    <Field
                      label="Select All Files"
                      type="checkbox"
                      value={folder.documents.every(doc => selectedDocs.includes(doc.id))}
                      onChange={() => handleFolderSelectAllToggle(folder.id)}
                    />
                  </Col>
                </Row>
                <ListGroup variant="flush">
                  {folder.documents.map((doc) => (
                    <ListGroup.Item key={doc.id} className="py-8 bg-transparent border-bottom border-gray-700">
                      <Row className="align-items-center">
                        <Col xs={1}>
                          <Field
                            type="checkbox"
                            value={selectedDocs.includes(doc.id)}
                            onChange={() => handleDocumentCheckboxChange(doc.id)}
                          />
                        </Col>
                        <Col xs={6}>
                          <p className="mb-0 fw-bold">{doc.name}</p>
                          <small className="text-info">
                            {moment(doc.updatedAt).format('MMM D, YYYY')}
                          </small>
                        </Col>
                        <Col xs={2}>
                          {doc.size && (
                            <small className="text-info">
                              {formatFileSize(doc.size)}
                            </small>
                          )}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            )}
          </Card>
        ))}
      </Accordion>
    </>
  );
};

export default FileSelector;
